import { useContext, useEffect, useRef, useState, useCallback } from "react";
import React from "react";
import { Field, Form, Formik } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import AppBar from "@material-ui/core/AppBar";
import {
  Box,
  DialogContent,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControlLabel,
  TextField,
  makeStyles,
  Grid,
  Typography,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  Select,
  MenuItem,
} from "@material-ui/core";
import Switch from "@material-ui/core/Switch";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import "./openingHourModal.css";
import EmojiDataPicker from "../EmojiDataPicker";
import { Can } from "../Can";
import QueueSelectConnection from "../Queue/queueSelectConnection";
import DatePickerBootstrap from "../../../app/components/DatePickerBootstrap";
import AddIcon from "@material-ui/icons/Add";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";

const useStyles = makeStyles((theme) => ({
  root: {
    flexWrap: "wrap",
    maxWidth: "500px",
  },
  container: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    width: "100%",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  textTagContainer: {
    width: "100%",
  },
  tab: {
    backgroundColor: "transparent",
    color: "#000",
    boxShadow: "none",
  },
  tabPanel: {
    overflowY: "auto",
  },
  emojiBox: {
    position: "absolute",
    zIndex: 10,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableRow: {
    borderBottom: "1px solid #ddd",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  tableCell: {
    padding: "8px",
    textAlign: "left",
  },
}));

const TimeInput = React.memo(
  ({ value, onChange, disabled, isBreakTime = false }) => {
    const inputRef = React.useRef(null);

    const handleBlur = () => {
      const current = inputRef.current?.value;
      if (current !== value) {
        onChange(current);
      }
    };

    return (
      <TextField
        type="time"
        defaultValue={isBreakTime ? value || "" : value || "09:00"}
        inputRef={inputRef}
        onBlur={handleBlur}
        InputLabelProps={{ shrink: true }}
        inputProps={{
          step: 300,
          pattern: "[0-9]{2}:[0-9]{2}",
        }}
        disabled={disabled}
        onFocus={(e) => e.target.select()}
      />
    );
  }
);

const SpecialHolidaysForm = React.memo(
  ({ onHolidaysChange, initialHolidays }) => {
    const [dateInput, setDateInput] = useState("");
    const [holidayInput, setHolidayInput] = useState("");
    const [holidays, setHolidays] = useState([]);
    const inputRef = useRef(null);

    useEffect(() => {
      // Verifica se initialHolidays tem conteúdo válido
      if (
        initialHolidays &&
        Array.isArray(initialHolidays) &&
        initialHolidays.length > 0
      ) {
        const validatedHolidays = initialHolidays
          .filter((h) => h.date && isValidDateString(h.date))
          .map((h) => ({
            ...h,
            date: h.date,
            id: h.id || Date.now(), // Garante que cada feriado tenha um ID
          }));
        setHolidays(validatedHolidays);
      } else {
        setHolidays([]); // Limpa a lista se não houver feriados
      }
    }, [initialHolidays]); // Executa sempre que initialHolidays mudar
    const isValidDateString = (dateString) => {
      const regex = /^\d{4}-\d{2}-\d{2}$/;
      if (!regex.test(dateString)) return false;

      const date = new Date(dateString);
      return !isNaN(date.getTime());
    };

    const formatDateForDisplay = (dateString) => {
      const [year, month, day] = dateString.split("-");
      return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
    };

    const handleAddHoliday = () => {
      if (!dateInput || !holidayInput.trim()) {
        toast.error("Preencha ambos os campos");
        return;
      }

      if (!isValidDateString(dateInput)) {
        toast.error("Data inválida");
        return;
      }

      if (holidays.some((h) => h.date === dateInput)) {
        toast.error("Esta data já foi adicionada");
        return;
      }

      const newHoliday = {
        date: dateInput,
        name: holidayInput.trim(),
        id: Date.now(),
      };

      const updatedHolidays = [...holidays, newHoliday];
      setHolidays(updatedHolidays);
      onHolidaysChange(updatedHolidays);

      setHolidayInput("");
      setDateInput("");
    };

    const handleRemove = (id) => {
      // Encontra o feriado que será removido
      const holidayToRemove = holidays.find((h) => h.id === id);

      // Remove o feriado especial da lista
      const updatedHolidays = holidays.filter((h) => h.id !== id);
      setHolidays(updatedHolidays);

      // Notifica o componente pai sobre a mudança
      onHolidaysChange(updatedHolidays, holidayToRemove?.date);
    };

    return (
      <div>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={5}>
            <TextField
              inputRef={inputRef}
              label="Nome do feriado"
              value={holidayInput}
              onChange={(e) => setHolidayInput(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={5}>
            <TextField
              type="date"
              label="Data do feriado"
              value={dateInput}
              onChange={(e) => setDateInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: new Date().toISOString().split("T")[0],
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={2}>
            <Button
              color="primary"
              onClick={handleAddHoliday}
              disabled={!dateInput || !holidayInput.trim()}
              variant="contained"
              fullWidth
              style={{ marginBottom: "-12px" }}
            >
              {i18n.t("buttons.add")}
            </Button>
          </Grid>
        </Grid>

        {holidays.length > 0 && (
          <Box mt={3}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const now = new Date();
                  const today = new Date(now);
                  today.setHours(0, 0, 0, 0); // zerar hora/minuto/segundo/milisegundo

                  const futureHolidays = holidays
                    .filter(h => {
                      const holidayDate = new Date(`${h.date} 00:00:00.000`);
                      holidayDate.setHours(0, 0, 0, 0); // normaliza
                      return holidayDate >= today;
                    })
                    .sort((a, b) => new Date(a.date) - new Date(b.date));

                  const pastHolidays = holidays
                    .filter(h => {
                      const holidayDate = new Date(`${h.date} 00:00:00.000`);
                      holidayDate.setHours(0, 0, 0, 0); // normaliza
                      return holidayDate < today;
                    })
                    .sort((a, b) => new Date(b.date) - new Date(a.date));


                  return (
                    <>
                      {/* Separador */}
                      {futureHolidays.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            Datas Agendadas
                          </TableCell>
                        </TableRow>
                      )}
                      {futureHolidays.map((holiday) => (
                        <TableRow key={`future-${holiday.id}`}>
                          <TableCell>{formatDateForDisplay(holiday.date)}</TableCell>
                          <TableCell>{holiday.name}</TableCell>
                          <TableCell>
                            <IconButton onClick={() => handleRemove(holiday.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Separador */}
                      {pastHolidays.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            Datas Concluídas
                          </TableCell>
                        </TableRow>
                      )}
                      {pastHolidays.map((holiday) => (
                        <TableRow key={`past-${holiday.id}`}>
                          <TableCell>{formatDateForDisplay(holiday.date)}</TableCell>
                          <TableCell>{holiday.name}</TableCell>
                          <TableCell>
                            <IconButton onClick={() => handleRemove(holiday.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  );
                })()}
              </TableBody>
            </Table>
          </Box>
        )}
      </div>
    );
  }
);

const OpeningHourModal = ({
  open,
  onClose,
  openingHourId,
  initialValues,
  onSave,
}) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [tabValue, setTabValue] = useState(0);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [queues, setQueues] = useState([]);
  const [actionAfterHour, setActionAfterHour] = useState({
    finish: false,
    transfer: false,
    queueId: null,
  });
  const [connectionsAndQueues, setConnectionsAndQueues] = useState([]);
  const [specialHolidays, setSpecialHolidays] = useState([]);

  const weekDays = [
    { name: "mon", label: "Segunda-feira" },
    { name: "tue", label: "Terça-feira" },
    { name: "wed", label: "Quarta-feira" },
    { name: "thu", label: "Quinta-feira" },
    { name: "fri", label: "Sexta-feira" },
    { name: "sat", label: "Sábado" },
    { name: "sun", label: "Domingo" },
  ];

  const initialState = {
    name: "",
    message: "",
    settings: {
      weekDays: [],
      dailyHours: {
        mon: {
          start: "09:00",
          end: "18:00",
          breakStart: "",
          breakEnd: "",
          active: false,
        },
        tue: {
          start: "09:00",
          end: "18:00",
          breakStart: "",
          breakEnd: "",
          active: false,
        },
        wed: {
          start: "09:00",
          end: "18:00",
          breakStart: "",
          breakEnd: "",
          active: false,
        },
        thu: {
          start: "09:00",
          end: "18:00",
          breakStart: "",
          breakEnd: "",
          active: false,
        },
        fri: {
          start: "09:00",
          end: "18:00",
          breakStart: "",
          breakEnd: "",
          active: false,
        },
        sat: {
          start: "09:00",
          end: "18:00",
          breakStart: "",
          breakEnd: "",
          active: false,
        },
        sun: {
          start: "09:00",
          end: "18:00",
          breakStart: "",
          breakEnd: "",
          active: false,
        },
      },
      holidays: [],
      actionAfterHour: {
        finish: false,
        transfer: false,
        queueId: null,
      },
      sendToOpenTickets: false,
      specialHolidays: [],
    },
    status: true,
    queues: [],
  };

  const [openingHour, setOpeningHour] = useState(initialState);

  const OpeningHourSchema = Yup.object().shape({
    name: Yup.string()
      .required(i18n.t("openingHourModal.invalidMessages.name.required"))
      .max(50, i18n.t("openingHourModal.invalidMessages.name.max")),
    message: Yup.string()
      .required(i18n.t("openingHourModal.invalidMessages.message.required"))
      .max(1024, i18n.t("openingHourModal.invalidMessages.message.max")),
    status: Yup.boolean().required(
      i18n.t("openingHourModal.invalidMessages.status.required")
    ),
    settings: Yup.object().shape({
      sendToOpenTickets: Yup.boolean(),
    }),
  });

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const fetchData = async () => {
      setLoading(true);
      try {
        const [holidaysRes, queuesRes, specialHolidaysRes] = await Promise.all([
          api.get("/holidays", { signal: abortController.signal }),
          api.get("/queue", { signal: abortController.signal }),
        ]);

        if (!isMounted) return;

        const holidaysData = holidaysRes.data;
        const queuesData = queuesRes.data;
        //const specialHolidaysData = specialHolidaysRes.data;

        setHolidays(holidaysData);

        if (!openingHourId) {
          setOpeningHour((prev) => ({
            ...prev,
            settings: {
              ...prev.settings,
              holidays: holidaysData.map((h) => h.date),
            },
          }));
        }

        queuesData.unshift({
          id: "selectAll",
          name: i18n.t("openingHourModal.buttons.selectAllQueues"),
        });
        setQueues(queuesData);
      } catch (error) {
        if (!isMounted || error.name === "AbortError") return;
        console.error("Erro ao buscar dados:", error);
        toastError(error);
      } finally {
        setLoading(false);
      }
    };

    const fetchSpecialHolidays = async () => {
      if (!open || !openingHourId) return;

      try {
        const { data } = await api.get(`/specialHolidays`, {
          signal: abortController.signal,
        });

        if (isMounted && data) {
          // Garante que os dados tenham o formato correto
          const formattedHolidays = data.flatMap((holidayGroup) =>
            holidayGroup.map((holiday) => ({
              id: holiday.id || Date.now(),
              name: holiday.name,
              date: holiday.date,
            }))
          );

          setSpecialHolidays(formattedHolidays);
        }
      } catch (err) {
        if (!isMounted || err.name === "AbortError") return;
        toastError(err);
      }
    };

    if (open) {
      fetchData();
      fetchSpecialHolidays();
    }

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [open, openingHourId]);

  useEffect(() => {
    if (!open || !openingHourId) return;
    let isMounted = true;
    const abortController = new AbortController();

    const fetchOpeningHour = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/openingHour/${openingHourId}`, {
          signal: abortController.signal,
        });
        if (!isMounted) return;
        const props = {
          name: data?.openingHours.name || "",
          message: data?.openingHours.message || "",
          settings: {
            weekDays: data?.openingHours.settings?.weekDays || [],
            dailyHours:
              data?.openingHours.settings?.dailyHours ||
              initialState.settings.dailyHours,
            holidays: data?.openingHours.settings?.holidays || [],
            actionAfterHour: data?.openingHours.settings?.actionAfterHour || {
              finish: false,
              transfer: false,
              queueId: null,
            },
            sendToOpenTickets:
              data?.openingHours.settings?.sendToOpenTickets || false,
            //specialHolidays: data?.openingHours.settings?.specialHolidays || [],
          },
          status:
            data?.openingHours.status == undefined
              ? true
              : data.openingHours.status,
        };

        //setSpecialHolidays(props.settings || []);

        setActionAfterHour({
          finish: props.settings.actionAfterHour.finish || false,
          transfer: props.settings.actionAfterHour.transfer || false,
          queueId: props.settings.actionAfterHour.queueId || null,
        });

        Object.keys(props.settings.dailyHours).forEach((day) => {
          if (
            !props.settings.dailyHours[day].breakStart ||
            !props.settings.dailyHours[day].breakEnd
          ) {
            props.settings.dailyHours[day] = {
              ...props.settings.dailyHours[day],
              breakStart: "",
              breakEnd: "",
            };
          }
        });

        setOpeningHour(props);

        setConnectionsAndQueues(
          data.openingHours.connectionsAndQueues &&
            Array.isArray(data.openingHours.connectionsAndQueues)
            ? data.openingHours.connectionsAndQueues
            : []
        );
      } catch (err) {
        if (!isMounted || err.name === "AbortError") return;
        console.error({ err });
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchOpeningHour();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [open, openingHourId]);

  function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box p={3}>
            <Typography>{children}</Typography>
          </Box>
        )}
      </div>
    );
  }

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  const validateForm = () => {
    try {
      OpeningHourSchema.validateSync(
        {
          name: openingHour.name,
          message: openingHour.message,
          status: openingHour.status,
        },
        { abortEarly: false }
      );

      if (openingHour.settings.weekDays.length === 0) {
        throw new Error(
          i18n.t("openingHourModal.invalidMessages.weekDays.required")
        );
      }

      if (actionAfterHour.transfer && !actionAfterHour.queueId) {
        throw new Error("Selecione uma fila para transferência");
      }

      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          toast.error(error.message);
        });
      } else {
        toast.error(err.message);
      }
      return false;
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    let checkSelectedOneQueue = false;
    if (
      connectionsAndQueues &&
      Array.isArray(connectionsAndQueues) &&
      connectionsAndQueues.length > 0
    ) {
      for (const cq of connectionsAndQueues) {
        if (
          cq?.queueIds &&
          Array.isArray(cq.queueIds) &&
          cq.queueIds.length > 0
        ) {
          checkSelectedOneQueue = true;
          break;
        }
      }
    }
    if (checkSelectedOneQueue == false)
      return toast.error("selecione pelo menos uma fila");

    setLoading(true);
    try {
      let dailyHoursToSave = { ...openingHour.settings.dailyHours };
      Object.keys(dailyHoursToSave).forEach((day) => {
        if (
          !dailyHoursToSave[day].breakStart ||
          !dailyHoursToSave[day].breakEnd
        ) {
          dailyHoursToSave[day] = {
            ...dailyHoursToSave[day],
            breakStart: "",
            breakEnd: "",
          };
        }
      });

      const filteredNormalHolidays = openingHour.settings.holidays.filter(
        (date) => !specialHolidays.some((sh) => sh.date === date)
      );

      // Feriados especiais (com nome e data)
      const formattedSpecialHolidays = specialHolidays.map((holiday) => ({
        name: holiday.name,
        date: holiday.date,
      }));

      // Concatena todas as datas únicas
      const allHolidayDates = [
        ...filteredNormalHolidays,
        ...formattedSpecialHolidays.map((h) => h.date),
      ];

      let openingProps = {
        ...openingHour,
        settings: {
          ...openingHour.settings,
          dailyHours: dailyHoursToSave,
          holidays: allHolidayDates,
          actionAfterHour: {
            finish: actionAfterHour.finish,
            transfer: actionAfterHour.transfer,
            queueId: actionAfterHour.queueId,
          },
          sendToOpenTickets: openingHour.settings.sendToOpenTickets,
        },
        specialHolidays: formattedSpecialHolidays,
        connectionsAndQueues,
      };

      if (!openingHourId) {
        await api.post("/openingHour", openingProps);
      } else {
        await api.put(`/openingHour/${openingHourId}`, openingProps);
      }

      toast.success(i18n.t(`openingHourModal.successMessages.save`));
      if (onSave) onSave();
      handleClose();
    } catch (err) {
      console.error({ err });
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const handleClose = () => {
    setOpeningHour(initialState);
    setActionAfterHour({
      finish: false,
      transfer: false,
      queueId: null,
    });
    setTabValue(0);
    setConnectionsAndQueues([]);
    setSpecialHolidays([]); // Limpa os feriados especiais
    setHolidays([]); // Limpa os feriados normais
    onClose(); // Chama a função onClose por último
  };

  const handleHolidaysChange = (e) => {
    if (!e?.target?.value) return;
    const val = e.target.value;

    // Verifica se a data já está nos feriados especiais
    if (specialHolidays.some((h) => h.date === val)) {
      toast.error("Esta data já está cadastrada como feriado especial");
      return;
    }

    const index = openingHour.settings.holidays.findIndex((sh) => sh == val);

    if (String(val).toLowerCase() == "selectall") {
      // Filtra para não incluir datas que já estão nos feriados especiais
      const allNormalHolidays = holidays
        .map((h) => h.date)
        .filter((date) => !specialHolidays.some((sh) => sh.date === date));

      if (index === -1) {
        setOpeningHour({
          ...openingHour,
          settings: {
            ...openingHour.settings,
            holidays: allNormalHolidays,
          },
        });
      } else {
        setOpeningHour({
          ...openingHour,
          settings: { ...openingHour.settings, holidays: [] },
        });
      }
      return;
    }

    let holidaysTmp = openingHour.settings.holidays;
    if (index === -1)
      setOpeningHour({
        ...openingHour,
        settings: { ...openingHour.settings, holidays: [...holidaysTmp, val] },
      });
    else
      setOpeningHour({
        ...openingHour,
        settings: {
          ...openingHour.settings,
          holidays: holidaysTmp.filter((sh) => sh != val),
        },
      });
  };

  const handleDayToggle = (dayName) => {
    const isActive = openingHour.settings.weekDays.includes(dayName);
    let newWeekDays = [...openingHour.settings.weekDays];

    if (isActive) {
      newWeekDays = newWeekDays.filter((day) => day !== dayName);
    } else {
      newWeekDays.push(dayName);
    }

    setOpeningHour((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        weekDays: newWeekDays,
        dailyHours: {
          ...prev.settings.dailyHours,
          [dayName]: {
            ...prev.settings.dailyHours[dayName],
            active: !isActive,
          },
        },
      },
    }));
  };

  const handleTimeChange = useCallback((dayName, field, value) => {
    setOpeningHour((prev) => {
      const newDailyHours = {
        ...prev.settings.dailyHours,
        [dayName]: {
          ...prev.settings.dailyHours[dayName],
          [field]: value,
        },
      };

      return {
        ...prev,
        settings: {
          ...prev.settings,
          dailyHours: newDailyHours,
        },
      };
    });
  }, []);

  const formatDates = (date) => {
    const parts = date.split("-");
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle
          id="form-dialog-title"
          style={{
            position: "sticky",
            top: "0",
            background: "#fff",
            zIndex: "10",
          }}
        >
          {openingHourId
            ? `${i18n.t("openingHourModal.title.edit")}`
            : `${i18n.t("openingHourModal.title.add")}`}
        </DialogTitle>
        <Formik
          initialValues={openingHour}
          enableReinitialize={true}
          onSubmit={(values, actions) => {
            handleSave();
          }}
        >
          {({ values }) => (
            <Form style={{ padding: "10px" }}>
              <DialogContent style={{ overflowY: "hidden" }}>
                <Grid container spacing={5}>
                  <Grid item sm={12} xs={12} mb={2}>
                    <Typography component="label">
                      {i18n.t("openingHourModal.form.name")}
                      <Typography component="span" className="text-danger ml-2">
                        *
                      </Typography>
                    </Typography>
                    <Field
                      style={{
                        borderColor: "#c4c4c4",
                        borderRadius: "3px",
                      }}
                      className="form-control"
                      placeholder={i18n.t("openingHourModal.form.name")}
                      name="name"
                      id="name"
                      autoFocus
                      maxLength={50}
                      onChange={(e) => {
                        setOpeningHour({
                          ...openingHour,
                          name: e.target.value,
                        });
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={5}>
                  <Grid item sm={12} xs={12} mb={2}>
                    <>
                      <Typography component="label">
                        {i18n.t("openingHourModal.form.connectionAndQueus")}
                        <Typography
                          component="span"
                          className="text-danger ml-2"
                        >
                          *
                        </Typography>
                      </Typography>
                      <div class="form-group col-md-12">
                        <Can
                          role={user.profile}
                          perform="user-modal:editQueues"
                          yes={() => (
                            <QueueSelectConnection
                              selecteds={connectionsAndQueues}
                              onSelected={(response) => {
                                if (
                                  response &&
                                  Array.isArray(response) &&
                                  response.length > 0
                                )
                                  setConnectionsAndQueues(response);
                              }}
                              isCollapsed={true}
                              filterQueue={(queue) =>
                                queue.name !== "FILA_CHATBOT"
                              }
                            />
                          )}
                        />
                      </div>
                    </>
                  </Grid>
                </Grid>

                <Grid container spacing={5}>
                  <Grid
                    item
                    sm={12}
                    xs={12}
                    mb={2}
                    style={{ position: "relative" }}
                  >
                    <Typography component="label">
                      {i18n.t("openingHourModal.form.message")}
                      <Typography component="span" className="text-danger ml-2">
                        *
                      </Typography>
                    </Typography>
                    <Field
                      style={{
                        borderColor: "#c4c4c4",
                        borderRadius: "3px",
                        height: "100px",
                      }}
                      className="form-control"
                      placeholder={i18n.t("openingHourModal.form.message")}
                      name="message"
                      id="message"
                      as="textarea"
                      type="message"
                      maxLength={1024}
                      onBlur={(e) => {
                        setOpeningHour({
                          ...openingHour,
                          message: e.target.value,
                        });
                      }}
                    />
                    <Box
                      style={{
                        width: "fit-content",
                        position: "absolute",
                        top: "42px",
                        right: "25px",
                      }}
                    >
                      <EmojiDataPicker
                        onEmojiSelect={(emoji) => {
                          setOpeningHour(() => {
                            if (
                              !isNaN(openingHour.message.length) &&
                              !isNaN(
                                document.querySelector("#message").maxLength
                              )
                            ) {
                              if (
                                openingHour.message.length >=
                                document.querySelector("#message").maxLength - 2
                              )
                                return values;
                            }
                            return {
                              ...openingHour,
                              message: `${openingHour.message} ${emoji}`,
                            };
                          });
                        }}
                      />
                    </Box>
                    <Typography>{`${openingHour.message.length}/1024`}</Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={5}>
                  <Grid item sm={12} xs={12} mb={2}>
                    <Typography component="label">
                      {i18n.t("openingHourModal.form.actionAfterMsgHour")}
                      <Typography component="span" className="text-danger ml-2">
                        *
                      </Typography>
                    </Typography>
                    <RadioGroup
                      row
                      name="afterHoursAction"
                      aria-label="Ação após envio da mensagem Fora de Horário"
                      value={actionAfterHour.transfer ? "transfer" : "finish"}
                      onChange={(e) => {
                        const value = e.target.value;
                        setActionAfterHour({
                          finish: value === "finish",
                          transfer: value === "transfer",
                          queueId:
                            value === "transfer"
                              ? actionAfterHour.queueId
                              : null,
                        });
                      }}
                    >
                      <FormControlLabel
                        value="finish"
                        control={<Radio color="primary" />}
                        label="Finalizar Atendimento"
                      />
                      <FormControlLabel
                        value="transfer"
                        control={<Radio color="primary" />}
                        label="Transferir Atendimento"
                      />
                    </RadioGroup>
                  </Grid>
                </Grid>

                {actionAfterHour.transfer && (
                  <Grid container spacing={5}>
                    <Grid item sm={12} xs={12} mb={2}>
                      <Typography component="label">
                        Selecione a fila para transferência
                        <Typography
                          component="span"
                          className="text-danger ml-2"
                        >
                          *
                        </Typography>
                      </Typography>
                      <Field
                        as={Select}
                        fullWidth
                        value={actionAfterHour.queueId || ""}
                        onChange={(e) => {
                          setActionAfterHour({
                            ...actionAfterHour,
                            queueId: e.target.value,
                          });
                        }}
                      >
                        {queues
                          .filter((queue) => queue.id !== "selectAll")
                          .map((queue) => (
                            <MenuItem key={queue.id} value={queue.id}>
                              {queue.name}
                            </MenuItem>
                          ))}
                      </Field>
                    </Grid>
                  </Grid>
                )}

                <Grid container spacing={5}>
                  <Grid item sm={12} xs={12} mb={2}>
                    <Typography component="label">
                      Enviar mensagem de "Fora de Horário" para atendimentos em
                      aberto com atendentes?
                    </Typography>
                    <Field
                      as={Switch}
                      color="primary"
                      name="settings.sendToOpenTickets"
                      checked={openingHour.settings.sendToOpenTickets}
                      onChange={(e) => {
                        setOpeningHour({
                          ...openingHour,
                          settings: {
                            ...openingHour.settings,
                            sendToOpenTickets: e.target.checked,
                          },
                        });
                      }}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item sm={12} xs={12} mb={2}>
                    <Typography component="label">
                      {i18n.t("openingHourModal.form.status")}
                      <Typography component="span" className="text-danger ml-2">
                        *
                      </Typography>
                    </Typography>
                    <Field
                      as={Switch}
                      color="primary"
                      name="status"
                      id="status"
                      checked={openingHour.status}
                      onChange={(e) => {
                        setOpeningHour({
                          ...openingHour,
                          status: e.target.checked,
                        });
                      }}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={5}>
                  <Grid item sm={12} xs={12} mb={2}>
                    <AppBar position="static" className={classes.tab}>
                      <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        aria-label="simple tabs"
                        TabIndicatorProps={{ style: { background: "#0b708c" } }}
                      >
                        <Tab
                          label={i18n.t("openingHourModal.form.tabs.weekdays")}
                          {...a11yProps(0)}
                        />
                        <Tab
                          label={i18n.t("openingHourModal.form.tabs.holidays")}
                          {...a11yProps(1)}
                        />
                        <Tab
                          label={i18n.t(
                            "openingHourModal.form.tabs.specialHolidays"
                          )}
                          {...a11yProps(2)}
                        />
                      </Tabs>
                    </AppBar>
                    <TabPanel
                      value={tabValue}
                      index={0}
                      className={classes.tabPanel}
                    >
                      <Grid container spacing={5}>
                        <Grid item sm={12} xs={12} mb={2}>
                          <Box style={{ marginTop: "10px", overflowX: "auto" }}>
                            <table className={classes.table}>
                              <thead>
                                <tr>
                                  <th className={classes.tableCell}>
                                    Dia da Semana
                                  </th>
                                  <th className={classes.tableCell}>
                                    Horário de Início
                                  </th>
                                  <th className={classes.tableCell}>
                                    Horário de Finalização
                                  </th>
                                  <th className={classes.tableCell}>
                                    Início do Intervalo
                                  </th>
                                  <th className={classes.tableCell}>
                                    Término do Intervalo
                                  </th>
                                  <th className={classes.tableCell}>Ativo</th>
                                </tr>
                              </thead>
                              <tbody>
                                {weekDays.map((day) => {
                                  const dayData =
                                    openingHour.settings.dailyHours[day.name] ||
                                    {};
                                  const isActive =
                                    openingHour.settings.weekDays.includes(
                                      day.name
                                    );

                                  return (
                                    <tr
                                      key={day.name}
                                      className={classes.tableRow}
                                    >
                                      <td className={classes.tableCell}>
                                        {day.label}
                                      </td>
                                      <td className={classes.tableCell}>
                                        <TimeInput
                                          value={dayData.start}
                                          onChange={(value) =>
                                            handleTimeChange(
                                              day.name,
                                              "start",
                                              value
                                            )
                                          }
                                          disabled={!isActive}
                                        />
                                      </td>
                                      <td className={classes.tableCell}>
                                        <TimeInput
                                          value={dayData.end}
                                          onChange={(value) =>
                                            handleTimeChange(
                                              day.name,
                                              "end",
                                              value
                                            )
                                          }
                                          disabled={!isActive}
                                        />
                                      </td>
                                      <td className={classes.tableCell}>
                                        <TimeInput
                                          value={dayData.breakStart}
                                          onChange={(value) =>
                                            handleTimeChange(
                                              day.name,
                                              "breakStart",
                                              value
                                            )
                                          }
                                          disabled={!isActive}
                                          isBreakTime={true}
                                        />
                                      </td>
                                      <td className={classes.tableCell}>
                                        <TimeInput
                                          value={dayData.breakEnd}
                                          onChange={(value) =>
                                            handleTimeChange(
                                              day.name,
                                              "breakEnd",
                                              value
                                            )
                                          }
                                          disabled={!isActive}
                                          isBreakTime={true}
                                        />
                                      </td>
                                      <td className={classes.tableCell}>
                                        <Switch
                                          checked={isActive}
                                          onChange={() =>
                                            handleDayToggle(day.name)
                                          }
                                          color="primary"
                                        />
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </Box>
                        </Grid>
                      </Grid>
                    </TabPanel>
                    <TabPanel
                      value={tabValue}
                      index={1}
                      className={classes.tabPanel}
                    >
                      {holidays.map((holiday) => (
                        <Grid container spacing={1} key={holiday.date}>
                          <Grid item sm={12} xs={12} mb={2}>
                            <FormControlLabel
                              color="success"
                              style={{ margin: "0px" }}
                              control={
                                <Checkbox
                                  checked={openingHour.settings.holidays.includes(
                                    holiday.date
                                  )}
                                  id={holiday.name}
                                  name={holiday.name}
                                  onChange={(e) => handleHolidaysChange(e)}
                                  value={holiday.date}
                                />
                              }
                              label={
                                String(holiday.date).toLowerCase() ==
                                "selectall"
                                  ? `${holiday.name}`
                                  : `${holiday.name} - ${formatDates(holiday.date)}`
                              }
                            />
                          </Grid>
                        </Grid>
                      ))}
                    </TabPanel>
                    <TabPanel value={tabValue} index={2}>
                      <SpecialHolidaysForm
                        onHolidaysChange={(updatedHolidays, removedDate) => {
                          setSpecialHolidays(updatedHolidays);

                          // Se uma data foi removida, também remove dos feriados normais
                          if (removedDate) {
                            setOpeningHour((prev) => ({
                              ...prev,
                              settings: {
                                ...prev.settings,
                                holidays: prev.settings.holidays.filter(
                                  (date) => date !== removedDate
                                ),
                              },
                            }));
                          }
                        }}
                        initialHolidays={specialHolidays}
                      />
                    </TabPanel>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions
                style={{
                  position: "sticky",
                  bottom: "0",
                  right: "0",
                  zIndex: "10",
                  background: "#fff",
                }}
              >
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={loading}
                  variant="outlined"
                >
                  {i18n.t("openingHourModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={loading}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {openingHourId
                    ? `${i18n.t("openingHourModal.buttons.okEdit")}`
                    : `${i18n.t("openingHourModal.buttons.okAdd")}`}
                  {loading && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default OpeningHourModal;
